<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-skeleton-loader v-if="loading || $apollo.loading" type="table" />
        <v-container v-else>
          <v-row>
            <v-col cols="12" class="text-center">
              <h2>Yesterday's chat metrics:</h2>
            </v-col>
            <v-col v-for="(metric, index) in chatGroupMetrics" :key="index" cols="3" align="center" justify="center">
              <metric-card :label="metric.label" :total="metric.total" />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-card class="pa-2">
                <v-row>
                  <v-col cols="4" class="pa-5 py-0">
                    <v-text-field v-model="filters.chatName" placeholder="Chat Name" />
                  </v-col>
                  <v-col cols="4" class="pa-5 py-0">
                    <v-checkbox v-model="filters.last7Days" label="Messages in the last 7 days" />
                  </v-col>
                  <v-col cols="4" class="pa-5 py-0">
                    <v-checkbox v-model="filters.isSystemCreated" label="System created" />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="6" class="px-5 py-0">
                    <v-select label="Chat Types" multiple v-model="filters.types" :items="chatTypes" />
                  </v-col>
                  <v-col cols="6" class="px-5 py-0">
                    <v-text-field v-model="filters.userName" placeholder="User Search (use email address)" />
                  </v-col>
                </v-row>
              </v-card>
            </v-col>

            <v-col>
              <v-card>
                <!-- i'm looping, but there is only one for now. be careful. The next item in the loop, if there ever is one
                may not fit nicely into the structure below.  -->
                <div v-for="(stat, index) in chatStats" :key="index" class="text-center pa-4">
                  <div class="ml-2 font-weight-bold">{{ stat.label }}</div>
                  <v-progress-circular :size="80" :width="15" :value="stat.total" color="teal">
                    {{ stat.total }}
                  </v-progress-circular>
                </div>
              </v-card>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-checkbox v-model="showParticipants" label="Show participants" />
            </v-col>
          </v-row>
          <v-simple-table>
            <thead>
              <tr>
                <th>Chat Name</th>
                <th>Participants</th>
                <th>Engaged</th>
                <th>Engaged %</th>
                <th>Total Messages</th>
                <th>Messages last 3 days</th>
                <th>Messages last 7 days</th>
                <th>Last Message Sent At</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(chat, key) in allChats.edges" :key="key">
                <td>
                  <a :href="`${$appEndPoint}/chats/${getId(chat.node.id)}`" target="_blank">
                    {{ chat.node.title }}</a
                  >
                </td>
                <td :width="showParticipants ? '50%' : ''">
                  <div v-if="showParticipants">
                    <span v-for="(user, k) in chat.node.users" :key="k">
                      <router-link :to="'/single-view/' + user.id"
                        >{{ user.firstName }} {{ user.lastName }}</router-link
                      >
                      |
                    </span>
                  </div>
                  <div v-else>
                    {{ chat.node.users.length }}
                  </div>
                </td>
                <td>
                  {{ chat.node.engaged }}
                </td>
                <td>{{ getEngagedPercent(chat.node.users.length, chat.node.engaged) }}</td>

                <td>{{ chat.node.totalMessages }}</td>
                <td>{{ chat.node.messagesThreeDays }}</td>
                <td>{{ chat.node.messagesSevenDays }}</td>
                <td>
                  <span v-if="chat.node.lastMessageTimestamp">{{
                    chat.node.lastMessageTimestamp | moment("ddd, DD MMM HH:MM")
                  }}</span>
                </td>
                <td>{{ chat.node.type }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <v-row>
            <v-col cols="6">
              <v-btn v-if="history.length > 0" @click="navigatePrevious">Previous Page</v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn class="float-right" v-if="pageInfo.hasNextPage" @click="navigateNext">Next Page</v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { ALL_CHATS, CHAT_GROUP_METRICS_QUERY, CHAT_STATS_QUERY } from "@/graphql/queries/chat";
import { decodeId } from "@/lib/string";
import MetricCard from "@/components/shared/MetricCard";

export default {
  name: "ChatListView",
  components: {
    MetricCard,
  },
  data() {
    return {
      loading: true,
      pageInfo: {
        hasNextPage: null,
        hasPreviousPage: null,
        startCursor: null,
        endCursor: null,
      },
      showParticipants: false,
      filters: {
        types: ["ACCOUNTABILITY"],
        last7Days: false,
        chatName: "",
        userName: "",
        isSystemCreated: false,
      },
      history: [],
      after: null,
      resultsPerPage: 100,
      chatTypes: ["ACCOUNTABILITY", "SUBURB", "COACH", "SUPPORT"],
    };
  },
  methods: {
    navigateNext() {
      this.history.push(this.after);
      this.after = this.pageInfo.endCursor;
      this.loading = true;
      window.scrollTo(0, 0);
    },
    navigatePrevious() {
      if (this.history.length === 0) return;
      this.after = this.history.pop();
      this.loading = true;
      this.$apollo.queries.allChats.refetch();
      window.scrollTo(0, 0);
    },
    getId(id) {
      return decodeId(id);
    },
    getEngagedPercent(totalUsers, engagedUsers) {
      if (totalUsers > 0 && engagedUsers > 0) {
        const percentage = Math.round((engagedUsers / totalUsers) * 100);
        return `${percentage}%`;
      }
      return null;
    },
  },
  apollo: {
    allChats: {
      query: ALL_CHATS,
      debounce: 1000,
      variables() {
        return {
          title: this.filters.chatName,
          after: this.after,
          first: this.resultsPerPage,
          type_In: this.filters.types,
          lastMessageTimestamp_Gte: this.filters.last7Days ? this.$moment().subtract(7, "days") : null,
          users_Username_Icontains: this.filters.userName,
          isSystemCreated: this.filters.isSystemCreated,
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data && !response.loading) {
          this.pageInfo = response.data.allChats.pageInfo;
          this.loading = false;
        }
      },
    },
    chatGroupMetrics: {
      query: CHAT_GROUP_METRICS_QUERY,
    },
    chatStats: {
      query: CHAT_STATS_QUERY,
      debounce: 1000,
      variables() {
        return {
          title: this.filters.chatName,
          typeIn: this.filters.types,
          lastMessageTimestampGte: this.filters.last7Days ? this.$moment().subtract(7, "days") : null,
          usernameContains: this.filters.userName,
          isSystemCreated: this.filters.isSystemCreated,
        };
      },
      fetchPolicy: "cache-and-network",
    },
  },
};
</script>
